import clsx from "clsx";
import { format, subMonths } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { Button, LoadPanel, Pagination, ScrollView } from "devextreme-react";
import { TextBox } from "devextreme-react/text-box";
import { Item, Toolbar } from "devextreme-react/toolbar";
import notify from "../../lib/notify";
import { produce } from "immer";
import upperFirst from "lodash/upperFirst";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  downloadAbsence,
  downloadAvailability,
  fetchSubordinateEmployees,
} from "../../api/calendarData";
import WorkPlanEditor from "../../components/work-plan-editor/WorkPlanEditor";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import axios from "../../lib/axios";
import {
  buildEmploymentIndex,
  EmploymentIndex,
  employmentKey,
  getEmploymentPeriods,
  isDayWithinEmployment,
} from "../../lib/employment";
import {
  CollectiveWorkPlan,
  PagedWorkPlanResultManager,
  WorkPlanEmployeeWithDates,
} from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./WorkPlanForManagementStaff.scss";

const currentDate = new Date();

const formatTime = (time: string) => {
  const dateObj = new Date(time);
  return dateObj.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatTimeWithoutSeconds = (timeString: string) => {
  const [hours, minutes] = timeString.split(":");
  return `${hours}:${minutes}`;
};

type DayInfo = {
  availabilityText: string;
  isRemote: boolean;
  isAbsence: boolean;
  absenceColor: string;
};

const DayCell = React.memo(function DayCell({
  day,
  dayInfo,
  isViolation,
  violationMessage,
  isSelected,
  outsideContract,
  outsideContractTitle,
  onClick,
  onDoubleClick,
}: {
  day: { firstLine: string; secondLine: string; dayType: number };
  dayInfo: DayInfo;
  isViolation: boolean;
  violationMessage: string | null;
  isSelected: boolean;
  outsideContract?: boolean;
  outsideContractTitle?: string;
  onClick: () => void;
  onDoubleClick: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      className={clsx(
        "p-3 flex flex-col justify-center items-center h-full rounded-lg",
        dayInfo.isRemote
          ? "bg-[#d7d7f2] text-black"
          : day.dayType === 2
            ? "bg-[#4d7c0f] text-white"
            : day.dayType === 3
              ? "bg-[#a3e635] text-black"
              : "bg-[#e9e9e9] text-black dark:bg-[#383838] dark:text-[#eaeaea]",
        isSelected && "selected-day",
        isViolation && "selected-red",
        outsideContract && "opacity-40 cursor-not-allowed",
      )}
      style={
        dayInfo.isAbsence
          ? { backgroundColor: dayInfo.absenceColor }
          : undefined
      }
      title={outsideContract ? outsideContractTitle : undefined}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <div>{day.firstLine}</div>
      <div>{day.secondLine}</div>
      <div className="text-center">{dayInfo.availabilityText}</div>
    </div>
  );
});

export default function WorkPlanForManagementStaff() {
  const { t, i18n } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [year, setYear] = useState(() => currentDate.getFullYear());
  const [month, setMonth] = useState(() => currentDate.getMonth());
  const [totalCount, setTotalCount] = useState(0);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entries, setEntries] = useState<CollectiveWorkPlan[]>([]);
  const [showWorkPlanEditor, setShowWorkPlanEditor] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth();
  const [availabilityData, setAvailabilityData] = useState<any[]>([]);
  const { companyId } = useCompanyContext();
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<number | null>(
    null,
  );
  const [users, setUsers] = useState<
    {
      id: number;
      companyId: number;
      code: number;
      firstName: string;
      lastName: string;
      contractType: number;
    }[]
  >([]);
  const [remoteWork] = useState<
    {
      id: number;
      employeeId: number;
      companyId: number;
      name: string;
      from: Date;
      to: Date;
      confirm: boolean;
      description?: string;
    }[]
  >([]);

  const [holidayData, setHolidayData] = useState<any[]>([]);
  // Przedziały zatrudnienia wyświetlonych pracowników - do wyszarzania kratek poza umową.
  const [employmentIndex, setEmploymentIndex] = useState<EmploymentIndex>(
    new Map(),
  );

  const availabilityMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const entry of availabilityData) {
      const key = `${entry.employeeId}_${entry.date}`;
      map.set(key, entry);
    }
    return map;
  }, [availabilityData]);

  const remoteWorkByEmployee = useMemo(() => {
    const map = new Map<number, typeof remoteWork>();
    for (const entry of remoteWork) {
      let list = map.get(entry.employeeId);
      if (!list) {
        list = [];
        map.set(entry.employeeId, list);
      }
      list.push(entry);
    }
    return map;
  }, [remoteWork]);

  useEffect(() => {
    if (!entries.length) return;
    const employeeIds = entries.map((e) => e.employee.id);
    const companyIds = entries.map((e) => e.employee.companyId);

    downloadAbsence(year, month + 1, employeeIds, companyIds)
      .then((res) => {
        if (res.isOk && Array.isArray(res.data)) {
          const flattened = res.data.flatMap((emp: any) =>
            (emp.absences || []).map((a: any) => ({
              employeeId: emp.employeeId,
              fromDate: a.fromDate,
              toDate: a.toDate,
              name: a.name,
              type: a.type,
            })),
          );
          setHolidayData(flattened);
        } else {
          setHolidayData([]);
        }
      })
      .catch(() => notify(t("errorOccurredWhileDataDownload"), "error"));
  }, [entries, month, year]);

  // Przedziały zatrudnienia dla wyświetlonych pracowników (widoczny miesiąc) - do blokady kratek poza umową.
  useEffect(() => {
    if (!entries.length) {
      setEmploymentIndex(new Map());
      return;
    }

    const employees = entries.map((e) => ({
      companyId: e.employee.companyId,
      employeeId: e.employee.id,
    }));
    const from = format(new Date(year, month, 1), "yyyy-MM-dd");
    const to = format(new Date(year, month + 1, 0), "yyyy-MM-dd");

    getEmploymentPeriods(employees, from, to)
      .then((periods) => setEmploymentIndex(buildEmploymentIndex(periods)))
      .catch(() => setEmploymentIndex(new Map())); // fail-open
  }, [entries, month, year]);

  useEffect(() => {
    getWorkPlanForDepartment();
    fetchDataForSubordinateEmployess();
  }, [companyId]);

  useEffect(() => {
    if (!user || typeof companyId !== "number") return;
    getWorkPlanForDepartment();
  }, [month, year, pageIndex, pageSize]);

  useEffect(() => {
    const delay = setTimeout(() => {
      getWorkPlanForDepartment();
    }, 400);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const fetchDataForAvailability = async (
    year: number,
    month: number,
    companyIds?: number[],
    employeeIds?: number[],
  ) => {
    const idsToFetch =
      employeeIds && employeeIds.length > 0
        ? employeeIds
        : user?.employeeId
          ? [user.employeeId]
          : [];

    if (idsToFetch.length === 0) return;

    const companyIdsToFetch =
      companyIds && companyIds.length > 0
        ? companyIds
        : user?.companyId
          ? [user.companyId]
          : [];

    if (companyIdsToFetch.length === 0) return;

    try {
      const response = await downloadAvailability(
        year,
        month,
        companyIdsToFetch,
        employeeIds,
      );

      if (!response.isOk || !response.data) {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );
        return;
      }

      const formattedData: any[] = [];

      response.data.forEach((employeeAvailability: any) => {
        const empId = employeeAvailability.employeeId;
        const entries = employeeAvailability.entries || [];

        entries.forEach((day: any) => {
          let positions = day.positions || [];

          if (positions.length === 0) {
            positions = [
              { from: "2025-02-27T08:00:00", to: "2025-02-27T16:00:00" },
            ];
          }

          formattedData.push({
            employeeId: empId,
            date: day.date.split("T")[0],
            available: !!day.sum,
            startTime: day.min || "",
            endTime: day.max || "",
            sum: day.sum || "",
            positions,
            notes: day.note || "",
            dateType: day.dateType || "",
          });
        });
      });

      setAvailabilityData(formattedData);
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const fetchDataForSubordinateEmployess = async () => {
    try {
      // let company = companyId === 0 ? user?.companyId || 0 : companyId;
      const toIso = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate(),
        ).padStart(2, "0")}`;
      const response = await fetchSubordinateEmployees(
        companyId,
        user?.employeeId || 0,
        false,
        toIso(new Date(year, month, 1)),
        toIso(new Date(year, month + 1, 0)),
      );
      if (response.isOk) {
        setUsers(response.data);
      } else {
        const usersList = response.data.data || response.data || [];
        setUsers(usersList);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const getDayInfo = useCallback(
    (dateStr: string, employeeId: number): DayInfo => {
      const formattedDate = dateStr.split("T")[0];
      const dateObj = new Date(dateStr);

      const absence = holidayData.find(
        (a) =>
          a.employeeId === employeeId &&
          new Date(a.fromDate) <= dateObj &&
          new Date(a.toDate) >= dateObj,
      );
      const isAbsence = !!absence;
      let absenceColor = "";
      if (isAbsence) {
        if (absence?.name?.toLowerCase().includes("urlop"))
          absenceColor = "#86efac";
        else if (absence?.name?.toLowerCase().includes("opieka"))
          absenceColor = "#facc15";
        else absenceColor = "#f87171";
      }

      const key = `${employeeId}_${formattedDate}`;
      const availabilityInfo = availabilityMap.get(key);

      const availabilityText =
        availabilityInfo?.available &&
        availabilityInfo.startTime &&
        availabilityInfo.endTime &&
        availabilityInfo.sum
          ? `* ${formatTime(availabilityInfo.startTime)}-${formatTime(
              availabilityInfo.endTime,
            )} (${formatTimeWithoutSeconds(availabilityInfo.sum)})`
          : "-";

      const employeeRemoteWork = remoteWorkByEmployee.get(employeeId);
      const isRemote =
        !!employeeRemoteWork &&
        employeeRemoteWork.some(
          (entry) =>
            new Date(entry.from) <= dateObj && new Date(entry.to) >= dateObj,
        );

      return {
        availabilityText,
        isRemote,
        isAbsence,
        absenceColor,
      };
    },
    [availabilityMap, holidayData, remoteWorkByEmployee],
  );

  const dayInfoCache = useMemo(() => {
    const cache = new Map<string, DayInfo>();
    for (const entry of entries) {
      for (const day of entry.days) {
        const cacheKey = `${entry.employee.id}_${day.date}`;
        cache.set(cacheKey, getDayInfo(day.date, entry.employee.id));
      }
    }
    return cache;
  }, [entries, getDayInfo]);

  const employeesWithSelectedDates = useMemo<
    WorkPlanEmployeeWithDates[]
  >(() => {
    return entries
      .filter((entry) => entry.days.some((day) => day.selected))
      .map((entry) => ({
        employee: {
          companyId: entry.employee.companyId,
          employeeId: entry.employee.id,
          name: `${entry.employee.lastName} ${entry.employee.firstName}`,
        },
        dates: entry.days
          .filter((day) => day.selected)
          .map((day) => new Date(day.date)),
      }));
  }, [entries]);

  const getWorkPlanForDepartment = async () => {
    setEntriesLoading(true);
    // let company = companyId === 0 ? user?.companyId || 0 : companyId;

    try {
      const response = await axios.get<PagedWorkPlanResultManager>(
        `/api/work-plan-for-department-employees/${companyId}`,
        {
          params: { year, month: month + 1, pageIndex, pageSize, searchTerm },
          headers: { "Accept-Language": i18n.language },
        },
      );

      const result = response.data;
      setTotalCount(result.totalCount);

      const items = result.items ?? [];
      const updated = items.map((entry) => ({
        ...entry,
        days: entry.days.map((day) => ({ ...day, selected: false })),
      }));

      setEntries(updated);
      const employeeIds = updated.map((e) => e.employee.id);
      const companyIds = updated.map((e) => e.employee.companyId);
      fetchDataForAvailability(year, month + 1, companyIds, employeeIds);
    } catch (err) {
      notify(t("errorOccurredWhileDataDownload"), "error");
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleCellClick = useCallback(
    (entryIndex: number, dayIndex: number) => {
      const clickedDate = new Date(entries[entryIndex].days[dayIndex].date);
      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );

      // Liczba dni roboczych (pon-pt) od początku bieżącego miesiąca do dziś.
      let workingDayCount = 0;
      for (let d = 1; d <= today.getDate(); d++) {
        const dayOfWeek = new Date(
          today.getFullYear(),
          today.getMonth(),
          d,
        ).getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workingDayCount++;
        }
      }

      // Edycja poprzedniego miesiąca dozwolona do 4. dnia roboczego włącznie;
      // od 5. dnia roboczego blokada już obowiązuje.
      const earliestAllowedMonthStart =
        workingDayCount < 5
          ? subMonths(currentMonthStart, 1)
          : currentMonthStart;
      const clickedMonthStart = new Date(
        clickedDate.getFullYear(),
        clickedDate.getMonth(),
        1,
      );

      if (clickedMonthStart < earliestAllowedMonthStart) {
        notify(t("workPlan.editPastMonthBlocked"), "warning");
        return;
      }

      // blokada dni poza okresem zatrudnienia pracownika (umowa do X / od Y)
      const cellEmployee = entries[entryIndex].employee;
      if (
        !isDayWithinEmployment(
          employmentIndex.get(
            employmentKey(cellEmployee.companyId, cellEmployee.id),
          ),
          clickedDate,
        )
      ) {
        notify(t("workPlan.outsideEmployment"), "warning");
        return;
      }

      setEntries((prev) =>
        produce(prev, (draft) => {
          const alreadySelected = draft[entryIndex].days.some(
            (d) => d.selected,
          );
          const anyoneElseSelected = draft.some(
            (e, i) => i !== entryIndex && e.days.some((d) => d.selected),
          );
          if (!alreadySelected && anyoneElseSelected) {
            draft.forEach((e, i) => {
              if (i !== entryIndex) {
                e.days.forEach((d) => {
                  d.selected = false;
                });
              }
            });
          }
          draft[entryIndex].days[dayIndex].selected =
            !draft[entryIndex].days[dayIndex].selected;
        }),
      );
    },
    [entries, employmentIndex, t],
  );

  const [popup, setPopup] = useState({
    visible: false,
    message: "",
    x: 0,
    y: 0,
  });

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent, message: string) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setPopup({
        visible: true,
        message,
        x: rect.left + window.scrollX + rect.width / 2,
        y: rect.top + window.scrollY + rect.height + 5,
      });
    },
    [],
  );

  const headerLabels = useMemo(() => {
    if (entries.length === 0) return [];
    const locale = i18n.language === "en" ? enUS : pl;
    return entries[0].days.map((day) => ({
      date: day.date,
      label: upperFirst(format(day.date, "EEEEEE, d.LL", { locale })),
    }));
  }, [entries, i18n.language]);

  const changeMonth = (direction: "previous" | "next") => {
    if (direction === "previous") {
      if (month === 0) {
        setMonth(11);
        setYear((y) => y - 1);
      } else {
        setMonth((m) => m - 1);
      }
    } else {
      if (month === 11) {
        setMonth(0);
        setYear((y) => y + 1);
      } else {
        setMonth((m) => m + 1);
      }
    }
  };

  const renderMonthNav = () => (
    <div className="flex items-center gap-1">
      <Button
        icon="chevronprev"
        stylingMode="text"
        onClick={() => changeMonth("previous")}
      />
      <div
        className={clsx(
          "text-center text-base",
          isMobile ? "flex-1 font-semibold" : "w-32",
        )}
      >
        {`${t("monthsList", { returnObjects: true })[month]} ${year}`}
      </div>
      <Button
        icon="chevronnext"
        stylingMode="text"
        onClick={() => changeMonth("next")}
      />
    </div>
  );

  return (
    <ScrollView useNative>
      <div className="p-6">
        {entriesLoading && (
          <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />
        )}
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("workPlan.header")}
        </div>
        <div className="mb-6 text-base">{t("workPlan.headerText")}</div>
        {showWorkPlanEditor && (
          <WorkPlanEditor
            employeesToSelect={users.map((user) => ({
              companyId: user.companyId,
              employeeId: user.id,
              name: `${user.lastName} ${user.firstName}`,
            }))}
            employeesWithDates={employeesWithSelectedDates}
            onUpdate={() => {
              getWorkPlanForDepartment();
              setShowWorkPlanEditor(false);
              setEntries(
                entries.map((entry) => ({
                  ...entry,
                  days: entry.days.map((day) => ({
                    ...day,
                    selected: false,
                  })),
                })),
              );
              notify(t("workPlan.changesToWorkPlanHaveBeenSaved"), "success");
            }}
            onClose={() => setShowWorkPlanEditor(false)}
            onDelete={async ({ companyId, employeeId, date }) => {
              await axios.delete("/api/work-plan", {
                params: {
                  companyId,
                  employeeId,
                  date: format(date, "yyyy-MM-dd"),
                },
                headers: {
                  "Accept-Language": i18n.language,
                },
              });
              getWorkPlanForDepartment();
              setShowWorkPlanEditor(false);
              setEntries(
                entries.map((entry) => ({
                  ...entry,
                  days: entry.days.map((day) => ({
                    ...day,
                    selected: false,
                  })),
                })),
              );
              notify(t("workPlanEditor.deleted"), "success");
            }}
          />
        )}

        {isMobile ? (
          <div className="mb-2 flex flex-col gap-2">
            {renderMonthNav()}
            <TextBox
              value={searchTerm}
              onValueChanged={(e) => setSearchTerm(e.value ?? "")}
              placeholder={t("Szukaj pracownika...")}
              showClearButton
              stylingMode="outlined"
              width="100%"
            />
            <Button
              text={t("workPlan.edit")}
              onClick={() => setShowWorkPlanEditor(true)}
              disabled={employeesWithSelectedDates.length === 0}
              type="default"
              width="100%"
            />
          </div>
        ) : (
          <Toolbar className="mb-2">
            <Item location="before" locateInMenu="auto">
              {renderMonthNav()}
            </Item>
            <Item location="after" locateInMenu="auto">
              <TextBox
                value={searchTerm}
                onValueChanged={(e) => setSearchTerm(e.value ?? "")}
                placeholder={t("Szukaj pracownika...")}
                showClearButton
                stylingMode="outlined"
                width={220}
              />
            </Item>
            <Item location="after" locateInMenu="auto">
              <Button
                text={t("workPlan.edit")}
                onClick={() => setShowWorkPlanEditor(true)}
                disabled={employeesWithSelectedDates.length === 0}
              />
            </Item>
          </Toolbar>
        )}

        {entries.length === 0 ? (
          <div
            className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
            role="alert"
          >
            {searchTerm
              ? t("Brak wyników dla wyszukiwania")
              : t("Brak danych do wyświetlenia")}
          </div>
        ) : isMobile ? (
          <div className="flex flex-col gap-3 pb-4">
            {entries.map((entry, entryIndex) => {
              const expanded = expandedEmployeeId === entry.employee.id;
              const hasSelection = entry.days.some((d) => d.selected);
              const isCivil = entry.employee.contractType === 20;
              return (
                <div
                  key={entry.employee.id}
                  className={clsx(
                    "rounded-lg border shadow-sm overflow-hidden bg-white dark:bg-[#1f1f1f]",
                    isCivil
                      ? "border-[3px] border-[#e4f226]"
                      : "border-gray-200 dark:border-gray-700",
                    hasSelection
                      ? "ring-[3px] ring-[#0ea5e9] ring-offset-2 ring-offset-white dark:ring-offset-[#1f1f1f]"
                      : "",
                  )}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedEmployeeId((prev) =>
                        prev === entry.employee.id ? null : entry.employee.id,
                      )
                    }
                    className="w-full text-left p-3 flex items-start gap-2 hover:bg-gray-50 dark:hover:bg-[#2a2a2a]"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">
                        {`${entry.employee.lastName} ${entry.employee.firstName}`}
                      </div>
                      <div className="text-xs opacity-90 mt-1 break-words">
                        {`${t("Zaplanowano")}: ${
                          entry.summary?.totalHours ?? "0:00"
                        } ${t("godz.")} / ${
                          entry.summary?.totalDays ?? 0
                        } ${t("dni")}`}
                      </div>
                    </div>
                    <i
                      className={clsx(
                        "dx-icon text-xl mt-1",
                        expanded ? "dx-icon-chevronup" : "dx-icon-chevrondown",
                      )}
                    />
                  </button>
                  {expanded ? (
                    <div className="flex flex-col gap-2 p-3 pt-0">
                      {entry.days.map((day, dayIndex) => {
                        const violation = entry.violations?.find((v) =>
                          v.affectedDays.includes(day.date),
                        );
                        const violationMessage = violation
                          ? violation.message
                          : null;
                        const isViolation = violationMessage !== null;
                        const cacheKey = `${entry.employee.id}_${day.date}`;
                        const dayInfo = dayInfoCache.get(cacheKey) ?? {
                          availabilityText: "-",
                          isRemote: false,
                          isAbsence: false,
                          absenceColor: "",
                        };
                        const dateObj = new Date(day.date);
                        const dayShort = upperFirst(
                          format(dateObj, "EEEEEE", {
                            locale: i18n.language === "en" ? enUS : pl,
                          }),
                        );
                        const outsideContract = !isDayWithinEmployment(
                          employmentIndex.get(
                            employmentKey(
                              entry.employee.companyId,
                              entry.employee.id,
                            ),
                          ),
                          day.date,
                        );
                        return (
                          <div
                            key={day.date}
                            className={clsx(
                              "flex items-stretch gap-3 p-3 rounded-lg cursor-pointer",
                              !dayInfo.isAbsence &&
                                (dayInfo.isRemote
                                  ? "bg-[#d7d7f2] text-black"
                                  : day.dayType === 2
                                    ? "bg-[#4d7c0f] text-white"
                                    : day.dayType === 3
                                      ? "bg-[#a3e635] text-black"
                                      : "bg-[#e9e9e9] text-black dark:bg-[#383838] dark:text-[#eaeaea]"),
                              day.selected
                                ? "border-[3px] border-[#0ea5e9] shadow-[0_0_0_2px_rgba(14,165,233,0.45)]"
                                : isViolation
                                  ? "border-2 border-red-500"
                                  : "border-2 border-transparent",
                              outsideContract && "opacity-40 cursor-not-allowed",
                            )}
                            style={
                              dayInfo.isAbsence
                                ? { backgroundColor: dayInfo.absenceColor }
                                : undefined
                            }
                            title={
                              outsideContract
                                ? t("workPlan.outsideEmployment")
                                : undefined
                            }
                            onClick={() =>
                              handleCellClick(entryIndex, dayIndex)
                            }
                          >
                            <div className="flex flex-col items-center justify-center min-w-[52px] font-bold">
                              <div className="text-[11px] uppercase opacity-80">
                                {dayShort}
                              </div>
                              <div className="text-2xl leading-none mt-1">
                                {format(dateObj, "d")}
                              </div>
                            </div>
                            <div className="flex-1 flex flex-col justify-center text-sm min-w-0">
                              <div>{day.firstLine}</div>
                              {day.secondLine ? (
                                <div className="text-xs opacity-85 break-words">
                                  {day.secondLine}
                                </div>
                              ) : null}
                              {dayInfo.availabilityText &&
                              dayInfo.availabilityText !== "-" ? (
                                <div className="text-xs opacity-85 break-words">
                                  {dayInfo.availabilityText}
                                </div>
                              ) : null}
                              {isViolation ? (
                                <div className="mt-1 text-xs text-white bg-red-600/90 rounded px-2 py-1 break-words">
                                  {violationMessage}
                                </div>
                              ) : null}
                            </div>
                            {day.selected ? (
                              <div className="flex items-center text-2xl">
                                <i className="dx-icon-check text-[#0ea5e9]"></i>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="work-plan-for-management-staff-table-wrapper overflow-auto"
            style={{ height: 521 }}
          >
            <table className="border-spacing-0 text-sm" style={{ height: 1 }}>
              <thead>
                <tr className="sticky top-0 z-[3] bg-[#fafafa] dark:bg-[#1f1f1f]">
                  <th className="sticky left-0 z-[4] p-1 whitespace-nowrap bg-[#fafafa] dark:bg-[#1f1f1f]">
                    <div className="p-3 bg-[#e2e2e2] dark:bg-[#292929] rounded-lg w-32 sm:w-60">
                      {t("workPlan.employee")}
                    </div>
                  </th>
                  {headerLabels.map((h) => (
                    <th key={h.date} className="p-1 whitespace-nowrap">
                      <div className="p-3 bg-[#e2e2e2] dark:bg-[#292929] rounded-lg min-w-32">
                        {h.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, entryIndex) => (
                  <tr key={entry.employee.id}>
                    <td className="sticky left-0 z-[2] p-1 bg-[#fafafa] dark:bg-[#1f1f1f]">
                      <div
                        className={clsx(
                          "w-32 sm:w-60 p-3 flex flex-col h-full rounded-lg",
                          entry.employee.contractType === 20
                            ? "bg-[#bfdbfe] text-black"
                            : "bg-[#e2e2e2] dark:bg-[#292929]",
                        )}
                      >
                        <div className="font-bold text-wrap truncate">
                          {`${entry.employee.lastName} ${entry.employee.firstName}`}
                        </div>
                        <div className="text-wrap truncate">
                          {`${t("Zaplanowano")}: ${
                            entry.summary?.totalHours ?? "0:00"
                          } ${t("godz.")} / ${
                            entry.summary?.totalDays ?? 0
                          } ${t("dni")}`}
                        </div>
                      </div>
                    </td>
                    {entry.days.map((day, dayIndex) => {
                      const violation = entry.violations?.find((v) =>
                        v.affectedDays.includes(day.date),
                      );
                      const violationMessage = violation
                        ? violation.message
                        : null;
                      const isViolation = violationMessage !== null;
                      const cacheKey = `${entry.employee.id}_${day.date}`;
                      const dayInfo = dayInfoCache.get(cacheKey) ?? {
                        availabilityText: "-",
                        isRemote: false,
                        isAbsence: false,
                        absenceColor: "",
                      };
                      const outsideContract = !isDayWithinEmployment(
                        employmentIndex.get(
                          employmentKey(
                            entry.employee.companyId,
                            entry.employee.id,
                          ),
                        ),
                        day.date,
                      );
                      return (
                        <td key={day.date} className="p-1 h-full">
                          <DayCell
                            day={day}
                            dayInfo={dayInfo}
                            isViolation={isViolation}
                            violationMessage={violationMessage}
                            isSelected={day.selected}
                            outsideContract={outsideContract}
                            outsideContractTitle={t(
                              "workPlan.outsideEmployment",
                            )}
                            onClick={() =>
                              handleCellClick(entryIndex, dayIndex)
                            }
                            onDoubleClick={(event) =>
                              isViolation &&
                              handleDoubleClick(event, violationMessage!)
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {popup.visible && (
              <div
                className="absolute bg-white p-2 shadow-lg rounded-lg border border-gray-300 text-black text-sm"
                style={{
                  top: popup.y,
                  left: popup.x,
                  transform: "translate(-48%, -300%)",
                }}
                onClick={() => setPopup({ ...popup, visible: false })}
              >
                {popup.message}
              </div>
            )}
          </div>
        )}

        <Pagination
          itemCount={totalCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          showInfo
          showPageSizeSelector
          showNavigationButtons
          onPageIndexChange={setPageIndex}
          onPageSizeChange={setPageSize}
          allowedPageSizes={[5, 10, 25, 50, 100]}
        />
      </div>
    </ScrollView>
  );
}
